/**
 * ExportColumnsModal — Configuration des colonnes CSV OPCO (Iter386 Lot B)
 *
 * Modale admin pour piloter les colonnes du CSV : activation, ordre, + délimiteur.
 * Données : catalog (clés disponibles) + settings (config actuelle).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X, Save, RotateCcw, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const DELIMITER_OPTIONS = [
  { id: ';', label: 'Point-virgule (Excel FR)' },
  { id: ',', label: 'Virgule' },
  { id: '\t', label: 'Tabulation' },
  { id: '|', label: 'Pipe' },
];

const ENCODING_OPTIONS = [
  { id: 'utf-8-sig', label: 'UTF-8 avec BOM (Excel)' },
  { id: 'utf-8', label: 'UTF-8' },
  { id: 'latin-1', label: 'Latin-1 (héritage)' },
];

export default function ExportColumnsModal({ open, onClose, api, token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [columns, setColumns] = useState([]);
  const [delimiter, setDelimiter] = useState(';');
  const [encoding, setEncoding] = useState('utf-8-sig');

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${api}/admin/compliance/export-settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error('Chargement échoué');
        const data = await r.json();
        setCatalog(data.catalog || []);
        setColumns(data.settings.columns || []);
        setDelimiter(data.settings.csv_delimiter || ';');
        setEncoding(data.settings.csv_encoding || 'utf-8-sig');
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, api, token]);

  const labelMap = useMemo(() => {
    const m = {};
    catalog.forEach((c) => { m[c.key] = c.label; });
    return m;
  }, [catalog]);

  const toggle = (key) => {
    setColumns((cs) => cs.map((c) => (c.key === key ? { ...c, enabled: !c.enabled } : c)));
  };

  const move = (idx, dir) => {
    setColumns((cs) => {
      const next = [...cs];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return cs;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${api}/admin/compliance/export-settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          columns: columns.map((c) => ({ key: c.key, enabled: !!c.enabled })),
          csv_delimiter: delimiter,
          csv_encoding: encoding,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Erreur sauvegarde');
      toast.success('Configuration enregistrée.');
      setColumns(data.settings.columns);
      onClose && onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-[#1F1A12]/40 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
      data-testid="export-columns-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
    >
      <div className="bg-white border border-[#E8E2D2] rounded-2xl w-full max-w-2xl max-h-[88vh] flex flex-col shadow-2xl">
        <header className="flex items-center gap-3 px-6 py-4 border-b border-[#E8E2D2]">
          <div className="flex-1">
            <h2 className="text-lg font-light text-[#1F1A12]" style={{ fontFamily: 'Georgia, serif' }}>
              Colonnes & format CSV
            </h2>
            <p className="text-xs text-[#6B5E48]">
              Pilote les colonnes incluses, leur ordre et le format CSV. Aucune valeur n'est codée en dur.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="export-columns-modal-close"
            className="p-2 rounded-full hover:bg-[#F3EFE3] text-[#6B5E48]"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-5 h-5 text-[#92710A] animate-spin" />
            </div>
          ) : (
            <>
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#6B5E48] font-semibold mb-2">
                  Colonnes ({columns.filter((c) => c.enabled).length} actives / {columns.length})
                </div>
                <ul className="border border-[#E8E2D2] rounded-xl divide-y divide-[#F0EADA]" data-testid="columns-list">
                  {columns.map((c, idx) => (
                    <li
                      key={c.key}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#FBF9F3]"
                      data-testid={`column-row-${c.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={!!c.enabled}
                        onChange={() => toggle(c.key)}
                        data-testid={`column-toggle-${c.key}`}
                        className="w-4 h-4 accent-[#C49B2A]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[#1F1A12]">{labelMap[c.key] || c.key}</div>
                        <div className="text-[10px] text-[#A89F8A] font-mono">{c.key}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                        className="p-1.5 rounded-md text-[#6B5E48] hover:bg-white disabled:opacity-30"
                        data-testid={`column-up-${c.key}`}
                      >
                        <ChevronUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => move(idx, 1)}
                        disabled={idx === columns.length - 1}
                        className="p-1.5 rounded-md text-[#6B5E48] hover:bg-white disabled:opacity-30"
                        data-testid={`column-down-${c.key}`}
                      >
                        <ChevronDown className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.14em] text-[#6B5E48] font-semibold block mb-1.5">
                    Séparateur
                  </label>
                  <select
                    data-testid="csv-delimiter-select"
                    value={delimiter}
                    onChange={(e) => setDelimiter(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-[#E8E2D2] bg-white text-sm text-[#1F1A12]"
                  >
                    {DELIMITER_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.14em] text-[#6B5E48] font-semibold block mb-1.5">
                    Encodage
                  </label>
                  <select
                    data-testid="csv-encoding-select"
                    value={encoding}
                    onChange={(e) => setEncoding(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-[#E8E2D2] bg-white text-sm text-[#1F1A12]"
                  >
                    {ENCODING_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}
        </div>

        <footer className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E8E2D2] bg-[#FBF9F3]">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border border-[#E8E2D2] text-[#6B5E48] hover:bg-white disabled:opacity-40"
            data-testid="export-columns-cancel"
          >
            <RotateCcw className="w-4 h-4" /> Annuler
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#1F1A12] shadow-[0_2px_8px_rgba(180,150,40,0.25)] hover:brightness-110 disabled:opacity-40"
            data-testid="export-columns-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Sauvegarder
          </button>
        </footer>
      </div>
    </div>
  );
}
